import { ReactElement } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material'
import { DeleteIcon, EditIcon } from 'src/v3/core/icons'
import { CourseAccreditationResponse, CourseAccreditationType } from '../types'

type AccreditationsSectionProps = {
  accreditation?: CourseAccreditationResponse
  isPublished: boolean
  isMigrating: boolean
  onMigrate: (accID: string) => void
  onEdit: (acc: CourseAccreditationResponse) => void
  onAdd: (type: CourseAccreditationType) => void
  onDelete: (accID: string) => void
  onUnpublish: () => void
  unpublishDisabled?: boolean
  onDeletePublished?: (accID: string) => void
}

export default function AccreditationsSection({
  accreditation,
  isPublished,
  isMigrating,
  onEdit,
  onAdd,
  onDelete,
  onMigrate,
  onUnpublish,
  unpublishDisabled,
  onDeletePublished,
}: AccreditationsSectionProps): ReactElement {
  if (!accreditation) {
    return (
      <Box className="flex flex-col gap-3 sm:flex-row">
        <Button
          size="large"
          variant="outlined"
          onClick={() => onAdd('accredited')}
          className="flex-1"
        >
          Accredited
        </Button>
        <Button
          size="large"
          variant="outlined"
          onClick={() => onAdd('notAccredited')}
          className="flex-1"
        >
          Not Accredited
        </Button>
      </Box>
    )
  }

  return (
    <>
      {accreditation.version === 1 && isPublished && (
        <Alert
          severity="warning"
          className="mb-4"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={onUnpublish}
              disabled={unpublishDisabled}
            >
              Unpublish
            </Button>
          }
        >
          This course must be unpublished before the accreditation can be
          migrated.
        </Alert>
      )}
      <Box className="flex items-center justify-between">
        <Box>
          <Typography variant="body2" className="font-semibold">
            {accreditation.accreditationType === 'accredited'
              ? 'Accredited'
              : 'Not Accredited'}
          </Typography>
          {accreditation.courseCode && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Course Code: {accreditation.courseCode}
            </Typography>
          )}
        </Box>
        <Box className="flex items-center">
          {accreditation.version === 1 ? (
            <Tooltip
              title={
                isPublished ? 'Course must be unpublished before migrating' : ''
              }
            >
              <span>
                <Button
                  size="small"
                  color="warning"
                  variant="outlined"
                  onClick={() => onMigrate(accreditation.id)}
                  disabled={isPublished || isMigrating}
                  aria-busy={isMigrating}
                  aria-label={
                    isPublished
                      ? 'Migrate accreditation — course must be unpublished first'
                      : 'Migrate accreditation'
                  }
                  startIcon={
                    isMigrating ? (
                      <CircularProgress size={12} color="inherit" />
                    ) : null
                  }
                >
                  {isMigrating ? 'Migrating...' : 'Migrate'}
                </Button>
              </span>
            </Tooltip>
          ) : (
            <>
              <IconButton
                size="small"
                onClick={() => onEdit(accreditation)}
                aria-label="Edit accreditation"
              >
                <EditIcon color="primary" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() =>
                  isPublished && onDeletePublished
                    ? onDeletePublished(accreditation.id)
                    : onDelete(accreditation.id)
                }
                disabled={isPublished && !onDeletePublished}
                aria-label={
                  isPublished && onDeletePublished
                    ? 'Unpublish course and delete accreditation'
                    : 'Delete accreditation'
                }
              >
                <DeleteIcon
                  color={
                    isPublished && !onDeletePublished ? 'disabled' : 'error'
                  }
                />
              </IconButton>
            </>
          )}
        </Box>
      </Box>
    </>
  )
}
